import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { Drawer, Button, Input } from "antd";
import { PlusOutlined, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { MdArrowBack } from "react-icons/md";
import classnames from "classnames";
import { observer } from "mobx-react";
import { getDirection } from "../../../helpers";
import { useStore } from "../../../store/rootStore";
import { ManagerFeature, ManagerOperation } from "../../../store/UIStore";
import { canManageFeature, getEntityIdKey } from "../constants";
import LabelsListView from "../managers/labels/LabelsListView";
import { ChannelsListView } from "../managers/channels";
import { AgentsListView } from "../managers/agents";
import { DepartmentsListView } from "../managers/departments";
import { ApiTokensListView } from "../managers/apiTokens";
import { OAuthListView } from "../managers/oauth";
import styles from "../SettingsDrawer.module.scss";
const FEATURE_TITLES = {
    [ManagerFeature.AGENTS]: "agentsManager",
    [ManagerFeature.DEPARTMENTS]: "departmentsManager",
    [ManagerFeature.LABELS]: "labelsManager",
    [ManagerFeature.CHANNELS]: "channelManager",
    [ManagerFeature.API_TOKENS]: "API Tokens",
    [ManagerFeature.OAUTH]: "oauthManager",
};
// Features that support creating new entities
const FEATURE_SUPPORTS_CREATE = new Set([
    ManagerFeature.LABELS,
    ManagerFeature.CHANNELS,
    ManagerFeature.AGENTS,
    ManagerFeature.DEPARTMENTS,
    ManagerFeature.API_TOKENS,
    ManagerFeature.OAUTH,
]);
const SecondLevelDrawer = observer(forwardRef(({ isOpen, className, onCloseDrawer, }, ref) => {
    const { ui, agentsStore } = useStore();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const listRefreshRef = useRef(null);
    useImperativeHandle(ref, () => ({
        refreshList: () => { var _a; return (_a = listRefreshRef.current) === null || _a === void 0 ? void 0 : _a.call(listRefreshRef); },
    }));
    const registerRefresh = (fn) => {
        listRefreshRef.current = fn;
    };
    const currentFeature = ui.getManagerFeature();
    const lastFeatureRef = React.useRef(null);
    if (currentFeature) {
        lastFeatureRef.current = currentFeature;
    }
    const displayFeature = currentFeature || lastFeatureRef.current;
    // Reset search when drawer closes or feature changes
    React.useEffect(() => {
        if (!isOpen) {
            setIsSearchOpen(false);
            setSearchQuery("");
        }
    }, [isOpen, currentFeature]);
    const title = displayFeature ? FEATURE_TITLES[displayFeature] : "";
    const handleSearchToggle = () => {
        if (isSearchOpen) {
            setSearchQuery("");
        }
        setIsSearchOpen(!isSearchOpen);
    };
    const handleEntitySelect = (entity) => {
        if (!displayFeature)
            return;
        const entityIdKey = getEntityIdKey(displayFeature);
        const entityId = entity[entityIdKey];
        ui.openThirdLevelManager(entityId, ManagerOperation.EDIT);
    };
    const handleEntityDelete = (entity) => {
        if (!displayFeature)
            return;
        const entityIdKey = getEntityIdKey(displayFeature);
        const entityId = entity[entityIdKey];
        ui.openThirdLevelManager(entityId, ManagerOperation.DELETE);
    };
    const handleCreateClick = () => {
        ui.openThirdLevelManager(null, ManagerOperation.CREATE);
    };
    const renderContent = () => {
        switch (displayFeature) {
            case ManagerFeature.LABELS:
                return (React.createElement(LabelsListView, { onEntitySelect: handleEntitySelect, onEntityDelete: handleEntityDelete, searchQuery: searchQuery, refreshList: registerRefresh }));
            case ManagerFeature.CHANNELS:
                return (React.createElement(ChannelsListView, { onEntitySelect: handleEntitySelect, onEntityDelete: handleEntityDelete, searchQuery: searchQuery, refreshList: registerRefresh }));
            case ManagerFeature.AGENTS:
                return (React.createElement(AgentsListView, { onEntitySelect: handleEntitySelect, onEntityDelete: handleEntityDelete, searchQuery: searchQuery, refreshList: registerRefresh }));
            case ManagerFeature.DEPARTMENTS:
                return (React.createElement(DepartmentsListView, { onEntitySelect: handleEntitySelect, onEntityDelete: handleEntityDelete, searchQuery: searchQuery, refreshList: registerRefresh }));
            case ManagerFeature.API_TOKENS:
                return (React.createElement(ApiTokensListView, { onEntitySelect: handleEntitySelect, onEntityDelete: handleEntityDelete, searchQuery: searchQuery, refreshList: registerRefresh }));
            case ManagerFeature.OAUTH:
                return (React.createElement(OAuthListView, { onEntitySelect: handleEntitySelect, onEntityDelete: handleEntityDelete, searchQuery: searchQuery, refreshList: registerRefresh }));
            default:
                return null;
        }
    };
    const showCreateButton = displayFeature
        ? FEATURE_SUPPORTS_CREATE.has(displayFeature) && canManageFeature(displayFeature, agentsStore.hasPermissions)
        : false;
    const headerButtons = (React.createElement("div", { className: styles.headerButtons },
        React.createElement(Button, { type: "text", icon: isSearchOpen ? React.createElement(CloseOutlined, null) : React.createElement(SearchOutlined, null), onClick: handleSearchToggle, className: styles.headerCreateButton }),
        showCreateButton && (React.createElement(Button, { type: "text", icon: React.createElement(PlusOutlined, null), onClick: handleCreateClick, className: styles.headerCreateButton }))));
    return (React.createElement(Drawer, { className: classnames(className, styles.drawer, styles.secondLevel, { [styles.rtl]: getDirection() === "rtl" }), onClose: onCloseDrawer, placement: getDirection() === "rtl" ? "right" : "left", open: isOpen, width: 368, title: React.createElement(Trans, { i18nKey: title }), extra: headerButtons, bodyStyle: { padding: 0 }, closeIcon: React.createElement(MdArrowBack, { className: styles.closeIcon }), maskClosable: true },
        isSearchOpen && (React.createElement("div", { className: styles.searchContainer },
            React.createElement(Input, { placeholder: "Search...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), prefix: React.createElement(SearchOutlined, null), allowClear: true, autoFocus: true }))),
        renderContent()));
}));
export default SecondLevelDrawer;
